import React from 'react';
import Grid from '@mui/material/Grid';
import { useAlert, useFetch } from '../../hooks';
import {
    Form,
    Field,
    ActionButton,
    GridDivider,
    FormType,
    InputEventProps,
    Alert
} from '..';

type attentionTypes = 'Prispek' | 'Spektra' | 'Prisma';
const attentions: Array<attentionTypes> = ['Prispek', 'Spektra', 'Prisma'];

interface queryProps {
    Attention: attentionTypes;
    Name: string;
    Email: string;
    Phone: string;
    Note: string;
}
const queryInitialState: queryProps = {
    Attention: 'Prispek',
    Name: '',
    Email: '',
    Phone: '',
    Note: ''
};

export const QueryForm: React.FC = React.memo(() => {
    const [fetchHandler, loading] = useFetch('/dbAddQuery', 'POST');
    const { alert, dispatchAlert } = useAlert();
    const [query, setQuery] = React.useState<queryProps>(queryInitialState);

    const onChangeHandler = React.useCallback(
        (key: keyof queryProps) => (e: InputEventProps) =>
            setQuery(prevQuery => {
                return {
                    ...prevQuery,
                    [key]: e.target.value
                };
            }),
        []
    );

    const errorHandler = React.useCallback(
        message => {
            dispatchAlert({
                type: 'UPDATE_ALERT',
                message: message,
                severity: 'error'
            });
        },
        [dispatchAlert]
    );

    const successHandler = React.useCallback(() => {
        dispatchAlert({
            type: 'UPDATE_ALERT',
            message: 'Success!',
            severity: 'success'
        });
    }, [dispatchAlert]);

    const queryHandler = React.useCallback(
        async (e: FormType) => {
            e.preventDefault();
            const result = await fetchHandler({ data: query });
            result.success ? successHandler() : errorHandler(result.msg);
            dispatchAlert({ type: 'OPEN' });
        },
        [fetchHandler, query, errorHandler, successHandler, dispatchAlert]
    );
    const attentionHandler = React.useCallback(
        (attention: attentionTypes) => () =>
            setQuery(prevQuery => {
                return { ...prevQuery, Attention: attention };
            }),
        []
    );

    return (
        <Form onSubmit={queryHandler} className='contact--book__form'>
            <Alert
                closeHandler={() => dispatchAlert({ type: 'CLOSE' })}
                {...alert}
            />
            <Grid item xs={12}>
                <h3 style={{ fontWeight: 500, color: '#555' }}>Recipient:</h3>
            </Grid>
            {attentions.map(attention => (
                <ActionButton
                    key={attention}
                    title={attention}
                    type='button'
                    sm={4}
                    sx={{
                        pt: '1.5rem',
                        pb: '1.5rem'
                    }}
                    color={
                        query.Attention === attention ? 'secondary' : 'inherit'
                    }
                    variant={
                        query.Attention === attention ? 'contained' : 'outlined'
                    }
                    onClick={attentionHandler(attention)}
                    disableElevation
                    disableFocusRipple
                    disableTouchRipple
                    grid
                />
            ))}
            <GridDivider />
            <Grid item xs={12}>
                <h3 style={{ fontWeight: 500, color: '#555' }}>
                    Your details:
                </h3>
            </Grid>
            <Field name='Name' onChange={onChangeHandler('Name')} />
            <Field name='Email' sm={6} onChange={onChangeHandler('Email')} />
            <Field
                name='Phone'
                sm={6}
                onChange={onChangeHandler('Phone')}
                optional
            />
            <Field
                name='Note'
                label='Notes'
                rows={3}
                onChange={onChangeHandler('Note')}
                multiline
            />
            <ActionButton
                title='Submit'
                type='submit'
                variant='contained'
                loading={loading}
                grid
            />
        </Form>
    );
});
